import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { TokensPayload } from "src/hooks/useAuth";
import { log } from "src/utils/logger";
import SnackbarUtils from "src/utils/SnackbarUtils";

import { apiClient, removeHeaderToken, setHeaderToken } from "./apiClient";
import { API } from "./apiConfig";

type Props = {
	signIn: (tokens: TokensPayload) => void;
	signOut: () => void;
};

type FailedQueueItem = {
	reject: (reason?: any) => void;
	resolve: (value?: any) => void;
	request: any;
};

let isRefreshing = false;
let refreshTokenPromise: Promise<string> | null = null;
let failedQueue: FailedQueueItem[] = [];

const requestQueue = {
	add(request: FailedQueueItem) {
		failedQueue.push(request);
	},
	flush(token: string) {
		const queue = [...failedQueue];
		failedQueue = [];

		queue.forEach(async (item) => {
			item.request.headers.Authorization = `Bearer ${token}`;
			try {
				const response = await apiClient(item.request);
				item.resolve(response);
			} catch (error) {
				item.reject(error);
			}
		});
	},
	clear(error: any) {
		failedQueue.forEach((item) => item.reject(error));
		failedQueue = [];
	}
};

const fetchNewToken = async () => {
	removeHeaderToken();
	const initialState = localStorage.getItem("authState");
	if (initialState) {
		const { refreshToken } = JSON.parse(initialState);
		try {
			const response = await apiClient.post(API.AUTH.REFRESH_TOKEN, { refreshToken });
			return response.data;
		} catch (error) {
			throw error;
		}
	}
	throw new Error("No refresh token found");
};

const refreshAuthLogic = async (signIn: (tokens: TokensPayload) => void) => {
	if (!refreshTokenPromise) {
		refreshTokenPromise = fetchNewToken()
			.then((newTokens) => {
				if (newTokens && newTokens.token && newTokens.refreshToken) {
					setHeaderToken(newTokens.token);
					signIn(newTokens);
					return newTokens.token;
				}
				throw new Error("Invalid token response");
			})
			.finally(() => {
				refreshTokenPromise = null;
			});
	}
	return refreshTokenPromise;
};

export const useAxiosInterceptorsSetup = ({ signIn, signOut }: Props) => {
	const queryClient = useQueryClient();

	const handleTokenRefresh = async () => {
		try {
			const token = await refreshAuthLogic(signIn);
			requestQueue.flush(token);
		} catch (error) {
			requestQueue.clear(error);
			SnackbarUtils.warning("La sesión ha expirado. Por favor vuelva a iniciar sesión.");
			removeHeaderToken();
			queryClient.removeQueries();
			signOut();
			throw error;
		}
	};

	useEffect(() => {
		apiClient.interceptors.response.use(
			(response) => {
				log(`${response.config.method}: ${response.config.url}`, response);
				return response;
			},
			async (error) => {
				const { config, response } = error;
				const originalRequest = config;

				if (response?.status === 401 && response?.config.url === API.AUTH.REFRESH_TOKEN) {
					requestQueue.clear(error);
					throw error;
				}

				if (response?.status === 401 && !originalRequest._retry) {
					originalRequest._retry = true;
					return new Promise((resolve, reject) => {
						requestQueue.add({ resolve, reject, request: originalRequest });
						if (!isRefreshing) {
							isRefreshing = true;
							handleTokenRefresh().finally(() => {
								isRefreshing = false;
							});
						}
					});
				}

				return Promise.reject(error);
			}
		);
	}, []);
};

let instance: any;
export default function (props: Props) {
	if (!instance) instance = useAxiosInterceptorsSetup(props as Props);
	return instance;
}
